import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Monitor, Cpu, Layers, Zap, Wrench, Code, CircuitBoard, Network } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { fetchCategories } from '../../api/categories'
import type { Category } from '../../types'
import { useLocalePath } from '../layout/LanguageRoute'
import { Spinner } from '../ui/spinner'

const CATEGORY_ICONS: Record<string, LucideIcon> = {
  'accessories':      Wrench,
  'motion-control':   Cpu,
  'hmi-panel-pcs':    Monitor,
  'x20-system':       Layers,
  'x90-system':       Layers,
  'controllers':      Cpu,
  'safety':           CircuitBoard,
  'software':         Code,
  'power-supplies':   Zap,
  'io-modules':       CircuitBoard,
  'communication':    Network,
}

function categoryIcon(slug: string): LucideIcon {
  return CATEGORY_ICONS[slug] ?? Layers
}

// PLC-forward order: controllers and PLC families first, their natural
// companions next (HMI, motion, I/O, power, safety, comms), pure accessories
// last. Categories not in this list fall through to the catalog count order
// at the end. See [[homepage-focus]] memory.
const PLC_PRIORITY_ORDER = [
  'controllers',
  'x20-system',
  'x90-system',
  'hmi-panel-pcs',
  'motion-control',
  'io-modules',
  'safety',
  'power-supplies',
  'communication',
  'software',
  'accessories',
]

function plcPriority(slug: string): number {
  const idx = PLC_PRIORITY_ORDER.indexOf(slug)
  return idx === -1 ? 999 : idx
}

export default function CategoryHighlights({ initialData }: { initialData?: Category[] } = {}) {
  const lp = useLocalePath()
  const { t } = useTranslation()
  const { data: categories, isLoading } = useQuery({
    queryKey: ['categories'],
    queryFn: fetchCategories,
    // SSR-seeded from pages/home/+data so the category grid is in the server HTML.
    initialData,
  })

  const sorted = [...(categories ?? [])]
    .sort((a, b) => {
      const ap = plcPriority(a.slug)
      const bp = plcPriority(b.slug)
      if (ap !== bp) return ap - bp
      // Tiebreak: catalog size, so unranked categories still surface biggest-first
      return (b.product_count ?? 0) - (a.product_count ?? 0)
    })
    .slice(0, 6)

  const total = (categories ?? []).reduce((sum, c) => sum + (c.product_count ?? 0), 0)

  return (
    <section id="producten" className="py-12 border-t border-neutral-100 scroll-mt-20">
      <div className="flex items-baseline justify-between mb-6 flex-wrap gap-2">
        <h2 className="text-[22px] font-bold text-gcg-text tracking-[-0.015em]">
          {t('shop.categories.heading')}
        </h2>
        <Link
          to={lp('/products')}
          className="text-sm font-medium text-primary hover:underline flex items-center gap-1"
        >
          {t('shop.categories.viewAll', { total: (!isLoading && categories) ? total.toLocaleString('nl-NL') : '…' })}
          <span aria-hidden="true">→</span>
        </Link>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {sorted.map(cat => {
            const Icon = categoryIcon(cat.slug)
            return (
              <Link
                key={cat.slug}
                to={lp(`/products?category=${encodeURIComponent(cat.name)}`)}
                className="flex flex-col gap-3 border border-gcg-border bg-white p-[18px] min-h-[130px] hover:border-primary hover:-translate-y-px transition-all duration-150"
              >
                <div className="flex items-center justify-center h-9 w-9 rounded-md bg-gcg-tint shrink-0">
                  <Icon className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gcg-text leading-snug mb-1">{cat.name}</h3>
                  {cat.product_count != null && (
                    <span className="text-xs text-neutral-500">{cat.product_count.toLocaleString('nl-NL')}</span>
                  )}
                </div>
              </Link>
            )
          })}
        </div>
      )}
    </section>
  )
}
